import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";

import { DffCurrentRoute, DffRoute } from "@dffedb/router";
import { trim } from "@dffedb/util";
import { HuskMigCredentialsService, LoginService } from "@e-forsyning/common/auth";
import { VaelgInstallationService } from "@e-forsyning/common/installation";
import { RedirectService } from "@e-forsyning/common/router";

import { LoginFormBuilder } from "../login-form-builder";
import { LoginContainerComponent } from "../login-container/login-container.component";
import { LoginInput } from "../login-input/login-input";
import { HttpClient } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { BiometricService } from "../biometrics/biometrics.service";
import { firstValueFrom } from "rxjs";
import { first } from "rxjs";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { TECHNICIAN_LOGIN_ROUTE } from "@e-forsyning/common/util/login-route.util";

@Component({
    selector: "login-technician",
    imports: [
        LoginContainerComponent,
        LoginInput,
        CommonModule,
    ],
    templateUrl: "./login-technician.html",
    styleUrls: ["../login.component.scss", "./login-technician.scss"]
})
export class LoginTechnician {
    public form: UntypedFormGroup;
    public loginFejl = false;
    public fejlBesked = "";
    public dashboardStatus: { freetext: string; login: boolean; frontpage: boolean; status: string } = { freetext: "", login: false, frontpage: false, status: "" };

    public isBiometricAvailable = false;
    public biometricActionText = "";
    public biometricEnabled = false;
    public biometricLoginInProgress = false;

    public technicianContactPhone = "12 13 14 15";
    public loadingIndstillinger = false;
    @ViewChild(LoginInput, { static: true }) public loginInput: LoginInput;

    constructor(
        private readonly currentRoute: DffCurrentRoute,
        private readonly huskMigCredentialsService: HuskMigCredentialsService,
        private readonly redirectService: RedirectService,
        private readonly loginService: LoginService,
        private readonly vaelgInstallationService: VaelgInstallationService,
        private readonly http: HttpClient,
        private readonly biometricService: BiometricService,
        private readonly umbracoIndstillingerObserver: UmbracoIndstillingerObserver
    ) {}

    public async ngOnInit(): Promise<void> {
        this.form = LoginFormBuilder.opretForm(this.huskMigCredentialsService);
        this.navigateToLoginPage();
        const erLoggetInd = this.loginService.erLoggetInd();
        if (erLoggetInd && this.umbracoIndstillingerObserver.initialized) {
            console.log("[login-debug] Er allerede logget ind, redirecting after login");
            this.redirectAfterLogin();
            return;
        }
        if (erLoggetInd) {
            this.loginService.logout();
        }

        this.getWarningMessage();
        await this.initTechnicianContactPhone();
        await this.initBiometricStatus();
    }

    private async initTechnicianContactPhone(): Promise<void> {
        try {
            const indstillinger = await firstValueFrom(this.umbracoIndstillingerObserver.valueChanges.pipe(first()));
            this.technicianContactPhone = indstillinger?.aktuelEforsyning?.firmaInfo?.telefon || "";
        } catch {
            this.technicianContactPhone = "";
        }
    }

    public navigateToLoginPage() {
        // Tekniker-login SKAL blive på tekniker-login ruten.
        // (Den generelle "login route preference" logik er til den almindelige login-side,
        // og vil ellers sende os over på /login.)
        const currentPath = trim(this.currentRoute.value.path, "/");
        const targetPath = trim(TECHNICIAN_LOGIN_ROUTE, "/");

        // Hvis vi ikke allerede står på korrekt login-side, naviger derhen
        // (allow nested paths like /login/xyz to count as /login)
        if (!currentPath.startsWith(targetPath)) {
            this.redirectService.redirect(targetPath);
        }
    }

    public async onLoginSucceeded(): Promise<void> {
        sessionStorage.setItem("biometricAutoLoginDone", "true");
        // First installation choice after technician login should land on tasks.
        sessionStorage.setItem("force-installation-redirect-route", "/opgaver");
        console.log("[login-debug] technician onLoginSucceeded");
        await this.vaelgInstallationService.vaelgFoeresteInstallation(false, this.form.value.forbrugerNrGroup.forbrugerNr);
        this.redirectAfterLogin();
    }

    public redirect(): void {
        const { path, params } = this.getRedirectPath();
        if (path && path !== "404") {
            this.redirectService.redirect(path, params);
        } else {
            this.redirectService.redirectTilDefault();
        }
    }

    private redirectAfterLogin(): void {
        const { path, params } = this.getRedirectPath();
        if (path && path !== "404") {
            console.log("[login-debug] redirectAfterLogin", { path, params });
            this.redirectService.redirect(path, params);
        } else {
            this.redirectService.redirectTilInstallationer();
        }
    }

    private getRedirectPath(): DffRoute {
        // Fjern "login" fra url'en og returner resten.
        // Eksempel: /login/mine-oplysninger -> mine-oplysninger
        const { path, params } = this.currentRoute.value;
        const [_, ...rest] = trim(path, "/").split("/");
        return { params, path: rest.join("/") };
    }

    public getWarningMessage() {
        this.http.get<{ freetext: string; login: boolean; frontpage: boolean; status: string }>("/umbraco/api/DashboardApi/GetDashboardFields").subscribe((data) => {
            this.dashboardStatus = data;
        });
    }

    private async initBiometricStatus(): Promise<void> {
        try {
            this.isBiometricAvailable = await this.biometricService.isAvailable();
            this.biometricEnabled = await this.biometricService.isBiometricLoginEnabled();
            this.biometricActionText = await this.biometricService.getBiometricActionText(this.biometricEnabled ? await this.biometricService.getBiometryType() : null);

            sessionStorage.getItem("biometricAutoLoginDone");
            if (!sessionStorage.getItem("biometricAutoLoginDone") && this.biometricEnabled) {
                sessionStorage.setItem("biometricAutoLoginDone", "true");
                setTimeout(() => {
                    this.loginUsingStoredCredentials();
                }, 200);
            }
        } catch {
            this.isBiometricAvailable = false;
            this.biometricEnabled = false;
        }
    }

    public async loginUsingStoredCredentials(): Promise<boolean | void> {
        try {
            const result = await this.biometricService.authenticate();
            if (!result) return false;

            this.loginFejl = false;
            this.fejlBesked = "";
            this.biometricLoginInProgress = true;

            this.loginService
                .login(result.userId, result.secret, false)
                .pipe(first())
                .subscribe({
                    next: async (res) => {
                        this.biometricLoginInProgress = false;
                        if (res.success) {
                            await this.onLoginSucceeded();
                        } else {
                            this.loginFejl = true;
                            this.fejlBesked = "Login mislykkedes";
                        }
                    },
                    error: (err) => {
                        this.biometricLoginInProgress = false;
                        this.loginFejl = true;
                        this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                    }
                });
        } catch (err: any) {
            this.biometricLoginInProgress = false;
            this.loginFejl = true;
            this.fejlBesked = err?.message ?? "Biometrisk autentificering mislykkedes";
            return false;
        }
    }

    public async biometricLogin(): Promise<void> {
        if (this.biometricLoginInProgress) return;

        this.loginFejl = false;
        this.fejlBesked = "";
        this.biometricLoginInProgress = true;

        try {
            const available = await this.biometricService.isAvailable();
            if (!available) {
                this.loginFejl = true;
                this.fejlBesked = "Biometrisk login virker ikke på denne enhed";
                this.biometricLoginInProgress = false;
                return;
            }

            const alreadyEnabled = await this.biometricService.isBiometricLoginEnabled();

            const email = this.form?.get("emailGroup.email")?.value;
            const password = this.form?.get("emailGroup.adgangskode")?.value;
            const technicianId = this.form?.get("forbrugerNrGroup.forbrugerNr")?.value;
            const technicianCode = this.form?.get("forbrugerNrGroup.kode")?.value;
            const formUserId = email || technicianId;
            const formSecret = password || technicianCode;

            // CASE A: Formular udfyldt med forbrugerNr/email og adgangskode — login med disse oplysninger og spørg om gemme nye oplysninger.
            if (formUserId && formSecret) {
                this.loginService
                    .login(String(formUserId), String(formSecret), false)
                    .pipe(first())
                    .subscribe({
                        next: async (res) => {
                            this.biometricLoginInProgress = false;
                            if (res.success) {
                                try {
                                    const wantToStore = confirm(
                                        alreadyEnabled
                                            ? "Vil du opdatere nye loginoplysninger for hurtigere login næste gang? Tryk 'OK' og godkend med biometrisk godkendelse."
                                            : "Vil du gemme loginoplysninger for hurtigere login næste gang? Tryk 'OK' og godkend med biometrisk godkendelse."
                                    );
                                    if (wantToStore) {
                                        try {
                                            await this.biometricService.enableBiometricLogin(
                                                String(formUserId),
                                                String(formSecret),
                                                alreadyEnabled ? "Opdater biometriske loginoplysninger" : "Aktiver biometrisk login",
                                                alreadyEnabled ? "Opdater biometriske loginoplysninger" : "Aktiver biometrisk login",
                                                alreadyEnabled
                                                    ? "Bekræft din identitet for at opdatere dine gemte loginoplysninger sikkert."
                                                    : "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                            );
                                            this.biometricEnabled = true;
                                            await this.onLoginSucceeded();
                                        } catch (err) {
                                            console.warn("Kunne ikke gemme/opdatere biometriske loginoplysninger:", err);
                                            alert("Kunne ikke gemme/opdatere biometriske loginoplysninger.");
                                            await this.onLoginSucceeded();
                                        }
                                    } else {
                                        await this.onLoginSucceeded();
                                    }
                                } catch {}
                            } else {
                                this.loginFejl = true;
                                this.fejlBesked = "Login mislykkedes";
                            }
                        },
                        error: (err) => {
                            this.biometricLoginInProgress = false;
                            this.loginFejl = true;
                            this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                        }
                    });
                return;
            }

            // CASE B: Ingen formularoplysninger — forsøg login med gemte biometriske oplysninger.
            const result = await this.biometricService.authenticate("Log ind med biometrisk godkendelse");
            if (!result) {
                this.loginFejl = true;
                this.fejlBesked = "Ingen biometriske loginoplysninger fundet";
                this.biometricLoginInProgress = false;
                return;
            }

            this.loginService
                .login(result.userId, result.secret, false)
                .pipe(first())
                .subscribe({
                    next: async (res) => {
                        this.biometricLoginInProgress = false;
                        if (res.success) {
                            await this.onLoginSucceeded();
                        } else {
                            this.loginFejl = true;
                            this.fejlBesked = "Login mislykkedes";
                        }
                    },
                    error: (err) => {
                        this.biometricLoginInProgress = false;
                        this.loginFejl = true;
                        this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                    }
                });
        } catch (err: any) {
            this.biometricLoginInProgress = false;
            const msg = String(err?.message || "").toLowerCase();
            if (msg.includes("cancel") || msg.includes("canceled") || msg.includes("user cancelled")) {
                this.loginFejl = false;
                this.fejlBesked = "";
                return;
            }
            this.loginFejl = true;
            this.fejlBesked = err?.message ?? "Biometrisk autentificering mislykkedes";
            return;
        }
    }
}
